/**
 * Короткий пример одной панорамной точки на посадочной.
 */
import { Viewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';

const SPOTS = [
  {
    id: 'loom',
    yaw: '8deg',
    pitch: '-18deg',
    kicker: 'Экспонат',
    title: 'Ткацкий станок',
    text: 'Деревянный ткацкий станок в центре зала. Справка показывает, что это за предмет.',
  },
  {
    id: 'cases',
    yaw: '-48deg',
    pitch: '-6deg',
    kicker: 'Карточка',
    title: 'Витрина',
    text: 'Клик открывает кадр и короткий текст: что это за предмет и зачем он в маршруте.',
  },
  {
    id: 'far',
    yaw: '168deg',
    pitch: '-4deg',
    kicker: 'Обзор',
    title: 'Дальняя стена',
    text: 'Эту метку видно, если повернуть панораму. Так гость находит точки за спиной.',
  },
  {
    id: 'task-text-mark',
    yaw: '-24deg',
    pitch: '8deg',
    taskId: 'task-text',
  },
  {
    id: 'task-choice-mark',
    yaw: '34deg',
    pitch: '6deg',
    taskId: 'task-choice',
  },
  {
    id: 'task-puzzle-mark',
    yaw: '8deg',
    pitch: '16deg',
    taskId: 'task-puzzle',
  },
];

const HERO_SPOTS = [
  {
    id: 'km-bench',
    yaw: '-16deg',
    pitch: '-2deg',
    kicker: 'Посадка',
    title: 'Диван у стены',
    text: 'Вдоль кирпича — деревянный диван и панели «горой». Гость сразу видит, куда сесть.',
  },
  {
    id: 'km-windows',
    yaw: '26deg',
    pitch: '-6deg',
    kicker: 'Свет',
    title: 'Столы у окон',
    text: 'Вторая посадка: столы, лампы и растения. Карточка открывается прямо на панораме.',
  },
  {
    id: 'km-choice',
    yaw: '-22deg',
    pitch: '-6deg',
    taskId: 'task-choice',
    question: {
      title: 'Что вдоль левой стены?',
      lead: 'Осмотритесь и выберите, что стоит слева от прохода.',
      options: [
        { value: 'bench', label: 'Деревянный диван' },
        { value: 'bar', label: 'Барная стойка' },
        { value: 'shelf', label: 'Открытые полки' },
      ],
      correct: 'bench',
      ok: 'Верно. Слева диван и деревянные панели.',
      bad: 'Не этот. Слева от прохода — диван у кирпича.',
    },
  },
  {
    id: 'km-battery',
    yaw: '117deg',
    pitch: '9deg',
    taskId: 'task-puzzle',
  },
  {
    id: 'km-chair',
    yaw: '128deg',
    pitch: '-13deg',
    taskId: 'task-cryptex',
  },
  {
    id: 'km-door',
    kind: 'door',
    yaw: '190deg',
    pitch: '2deg',
    kicker: 'Переход',
    title: 'Выйти в дверь',
    text: 'В демонстрации переход недоступен.',
  },
  {
    id: 'km-floor',
    kind: 'floor',
    yaw: '8deg',
    pitch: '-24deg',
    kicker: 'Переход',
    title: 'Перейти по полу',
    text: 'В демонстрации переход недоступен.',
  },
  {
    id: 'km-text',
    yaw: '10deg',
    pitch: '4deg',
    taskId: 'task-text',
    question: {
      title: 'Цвет стен',
      lead: 'Какого цвета кирпичные стены в этом зале?',
      accept: 'син|голуб',
      ok: 'Да, кладка выкрашена в голубой.',
      bad: 'Посмотрите на кладку: она голубая.',
    },
  },
];

const BACKPACK = [
  [844, 968], [912, 968], [908, 992], [880, 1016], [1012, 1040],
  [1044, 1064], [1064, 1088], [1068, 1112], [1072, 1136], [1076, 1160],
  [1072, 1184], [1080, 1208], [1084, 1232], [1088, 1256], [1088, 1280],
  [1096, 1304], [1108, 1328], [1112, 1352], [1128, 1376],
  [1124, 1392], [1124, 1696], [1100, 1696], [1100, 1392],
  [828, 1376],
  [808, 1392], [808, 1640], [784, 1640], [784, 1392],
  [792, 1352], [788, 1328], [792, 1304], [796, 1280], [796, 1256],
  [792, 1232], [796, 1208], [804, 1184], [808, 1160], [812, 1136],
  [820, 1112], [840, 1088], [864, 1064], [968, 1040], [812, 1016],
  [788, 992], [844, 968],
];

function mountFindOverlay(container) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'pitch-find');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = `
    <defs>
      <mask id="pitch-find-mask" maskUnits="userSpaceOnUse">
        <rect class="pitch-find__fill" fill="white"></rect>
        <path class="pitch-find__hole" fill="black"></path>
      </mask>
    </defs>
    <rect class="pitch-find__shade" mask="url(#pitch-find-mask)"></rect>
    <path class="pitch-find__stroke" fill="none"></path>
    <path class="pitch-find__leader" fill="none"></path>
    <circle class="pitch-find__dot" r="5"></circle>
  `;
  const note = document.createElement('div');
  note.className = 'pitch-find__note';
  note.setAttribute('role', 'status');
  const noteTitle = document.createElement('span');
  noteTitle.className = 'pitch-find__note-title';
  noteTitle.textContent = 'Рюкзак найден';
  const noteBody = document.createElement('span');
  noteBody.textContent = 'Поиск скрытых предметов. Одна из игровых механик в\u00a0формате Квест';
  const noteClose = document.createElement('button');
  noteClose.type = 'button';
  noteClose.className = 'pitch-find__note-close';
  noteClose.textContent = 'Закрыть';
  note.append(noteTitle, noteBody, noteClose);
  container.append(svg, note);
  sizeFindOverlay(svg, container);
  return svg;
}

function sizeFindOverlay(svg, container) {
  const width = container.clientWidth;
  const height = container.clientHeight;
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.querySelector('.pitch-find__fill').setAttribute('width', width);
  svg.querySelector('.pitch-find__fill').setAttribute('height', height);
  svg.querySelector('.pitch-find__shade').setAttribute('width', width);
  svg.querySelector('.pitch-find__shade').setAttribute('height', height);
}

function syncFindOverlay(svg, marker) {
  const source = marker && marker.domElement;
  const path = source && source.getAttribute('d');
  const hole = svg.querySelector('.pitch-find__hole');
  const stroke = svg.querySelector('.pitch-find__stroke');
  if (!path) {
    hole.removeAttribute('d');
    stroke.removeAttribute('d');
    return;
  }
  const transform = source.getAttribute('transform') || '';
  hole.setAttribute('d', path);
  hole.setAttribute('transform', transform);
  stroke.setAttribute('d', path);
  stroke.setAttribute('transform', transform);
  placeFindCallout(svg, source);
}

function placeFindCallout(svg, source) {
  const host = svg.parentElement.getBoundingClientRect();
  const box = source.getBoundingClientRect();
  const note = svg.parentElement.querySelector('.pitch-find__note');
  const leader = svg.querySelector('.pitch-find__leader');
  const dot = svg.querySelector('.pitch-find__dot');
  if (!note || !box.width || !box.height) return;
  if (window.matchMedia('(pointer: coarse)').matches) {
    note.style.transform = '';
    return;
  }

  const dotX = box.left - host.left + box.width * 0.52;
  const dotY = box.top - host.top + Math.min(box.height * 0.28, 210);
  const noteWidth = note.offsetWidth || 220;
  const noteHeight = note.offsetHeight || 72;
  const gap = 64;
  let noteX = dotX + gap;
  if (noteX + noteWidth > host.width - 16) noteX = dotX - gap - noteWidth;
  noteX = Math.max(12, Math.min(noteX, host.width - noteWidth - 12));
  let noteY = dotY - noteHeight * 0.35;
  noteY = Math.max(12, Math.min(noteY, host.height - noteHeight - 12));
  const targetX = noteX > dotX ? noteX : noteX + noteWidth;
  const targetY = noteY + noteHeight / 2;
  const elbowX = dotX + (targetX - dotX) * 0.45;

  dot.setAttribute('cx', dotX);
  dot.setAttribute('cy', dotY);
  leader.setAttribute('d', `M${dotX} ${dotY} L${elbowX} ${dotY} L${targetX} ${targetY}`);
  note.style.transform = `translate(${noteX}px, ${noteY}px)`;
}

function floorLayer() {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pitch-floor';
  button.setAttribute('aria-label', 'Переход по полу');
  button.innerHTML = '<span class="hs hs--floor"><span class="hs__floor-ring" aria-hidden="true"></span></span>';
  return button;
}

function markerHtml(spot) {
  if (spot.kind === 'door') {
    return '<button type="button" class="pitch-door" aria-label="Переход через дверь"><span class="hs hs--nav hs--caption-off"><span class="hs__beacon" aria-hidden="true"><span class="hs__ring hs__ring--a"></span><span class="hs__ring hs__ring--b"></span><span class="hs__core"></span></span></span></button>';
  }
  if (spot.taskId) {
    return '<button type="button" class="pitch-hotspot pitch-hotspot--task" aria-label="Вопрос">?</button>';
  }
  return '<button type="button" class="pitch-hotspot" aria-label="Информация">i</button>';
}

function markerConfig(spot) {
  if (spot.kind === 'floor') {
    return {
      id: spot.id,
      position: { yaw: spot.yaw, pitch: spot.pitch },
      elementLayer: floorLayer(),
      size: { width: 240, height: 240 },
      rotation: { pitch: Math.PI / 2 },
      anchor: 'center center',
      hoverScale: false,
      className: 'psv-marker--hit psv-marker--floor',
      data: spot,
    };
  }
  const door = spot.kind === 'door';
  return {
    id: spot.id,
    position: { yaw: spot.yaw, pitch: spot.pitch },
    html: markerHtml(spot),
    size: door ? { width: 54, height: 54 } : { width: 44, height: 44 },
    anchor: 'center center',
    hoverScale: false,
    data: spot,
  };
}

const modal = document.getElementById('pitch-modal');
const infoKicker = document.getElementById('pitch-info-kicker');
const infoTitle = document.getElementById('pitch-info-title');
const infoText = document.getElementById('pitch-info-text');
const choiceSheet = document.getElementById('task-choice');
const choiceLead = choiceSheet ? choiceSheet.querySelector('.pitch-sheet__lead') : null;
const choiceTitle = choiceSheet ? choiceSheet.querySelector('h3') : null;
const choiceOptions = choiceSheet ? choiceSheet.querySelector('.pitch-options') : null;
const textSheet = document.getElementById('task-text');
const textLead = textSheet ? textSheet.querySelector('.pitch-sheet__lead') : null;
const textTitle = textSheet ? textSheet.querySelector('h3') : null;
const defaultChoiceHTML = choiceOptions ? choiceOptions.innerHTML : '';
const defaultChoiceLead = choiceLead ? choiceLead.textContent : '';
const defaultChoiceTitle = choiceTitle ? choiceTitle.textContent : '';
const defaultTextLead = textLead ? textLead.textContent : '';
const defaultTextTitle = textTitle ? textTitle.textContent : '';

function openPanel(id) {
  if (!modal) return;
  modal.querySelectorAll('.pitch-sheet').forEach((sheet) => {
    sheet.hidden = sheet.id !== id;
  });
  if (!modal.open) modal.showModal();
  if (id === 'task-text') {
    const field = document.getElementById('task-text-answer');
    if (field) field.focus();
  }
}

function fillChoice(question) {
  if (!choiceOptions || !choiceLead || !choiceTitle) return;
  choiceTitle.textContent = question.title;
  choiceLead.textContent = question.lead;
  choiceOptions.replaceChildren();
  question.options.forEach((option) => {
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'pitch-choice';
    input.value = option.value;
    label.append(input, document.createTextNode(` ${option.label}`));
    choiceOptions.append(label);
  });
  choiceOptions.dataset.correct = question.correct;
  choiceOptions.dataset.ok = question.ok;
  choiceOptions.dataset.bad = question.bad;
}

function resetChoice() {
  if (!choiceOptions || !choiceLead || !choiceTitle) return;
  choiceTitle.textContent = defaultChoiceTitle;
  choiceLead.textContent = defaultChoiceLead;
  choiceOptions.innerHTML = defaultChoiceHTML;
  delete choiceOptions.dataset.correct;
  delete choiceOptions.dataset.ok;
  delete choiceOptions.dataset.bad;
}

function fillText(question) {
  if (!textSheet || !textLead || !textTitle) return;
  textTitle.textContent = question.title;
  textLead.textContent = question.lead;
  textSheet.dataset.accept = question.accept;
  textSheet.dataset.ok = question.ok;
  textSheet.dataset.bad = question.bad;
}

function resetText() {
  if (!textSheet || !textLead || !textTitle) return;
  textTitle.textContent = defaultTextTitle;
  textLead.textContent = defaultTextLead;
  delete textSheet.dataset.accept;
  delete textSheet.dataset.ok;
  delete textSheet.dataset.bad;
}

function openSpot(spot) {
  if (!spot) return;
  if (spot.taskId === 'task-choice') {
    if (spot.question) fillChoice(spot.question);
    else resetChoice();
    const status = document.querySelector('[data-task-choice-status]');
    if (status) status.textContent = '';
    if (choiceSheet) choiceSheet.classList.remove('is-ok', 'is-bad');
    openPanel('task-choice');
    return;
  }
  if (spot.taskId === 'task-text') {
    if (spot.question) fillText(spot.question);
    else resetText();
    const status = document.querySelector('[data-task-text-status]');
    if (status) status.textContent = '';
    if (textSheet) textSheet.classList.remove('is-ok', 'is-bad');
    openPanel('task-text');
    return;
  }
  if (spot.taskId) {
    openPanel(spot.taskId);
    return;
  }
  if (infoKicker) infoKicker.textContent = spot.kicker || '';
  if (infoTitle) infoTitle.textContent = spot.title || '';
  if (infoText) infoText.textContent = spot.text || '';
  openPanel('pitch-info');
}

if (modal) {
  modal.addEventListener('click', (event) => {
    if (event.target === modal) modal.close();
  });
  const modalClose = modal.querySelector('[data-modal-close]');
  if (modalClose) modalClose.addEventListener('click', () => modal.close());
}

const viewerLang = {
  loading: 'Загрузка…',
  loadError: 'Не удалось загрузить панораму',
  webglError: 'Браузер не поддерживает WebGL',
  twoFingers: 'Для обзора используйте два пальца',
  ctrlZoom: 'Чтобы увеличить, зажмите Ctrl и крутите колёсико',
};

const hero = document.getElementById('pitch-hero-viewer');
if (hero && hero.dataset.panorama) {
  const heroViewer = new Viewer({
    container: hero,
    panorama: hero.dataset.panorama,
    navbar: false,
    lang: viewerLang,
    mousewheel: false,
    touchmoveTwoFingers: true,
    defaultYaw: '0deg',
    defaultPitch: '-6deg',
    defaultZoomLvl: 30,
    plugins: [
      MarkersPlugin.withConfig({
        markers: [
          ...HERO_SPOTS.map(markerConfig),
          {
            id: 'km-backpack',
            polygonPixels: BACKPACK,
            svgStyle: {
              fill: 'transparent',
              stroke: 'none',
              pointerEvents: 'visibleFill',
            },
          },
        ],
      }),
    ],
  });
  const heroFrame = hero.closest('.pitch-hero__preview');
  const heroMarkers = heroViewer.getPlugin(MarkersPlugin);
  const findOverlay = mountFindOverlay(hero);
  hero.addEventListener('pointerdown', () => {
    if (heroFrame) heroFrame.classList.add('is-looking');
  }, true);
  const findTask = document.getElementById('pitch-find-task');
  function coarsePointer() {
    return window.matchMedia('(pointer: coarse)').matches;
  }
  function markBackpackFound() {
    if (!findTask) return;
    findTask.classList.add('is-found');
    const status = findTask.querySelector('.pitch-find-task__status');
    if (status) status.hidden = false;
  }
  function setFind(open) {
    if (!heroFrame) return;
    const marker = heroMarkers.getMarker('km-backpack');
    if (open) {
      heroFrame.classList.add('is-find');
      syncFindOverlay(findOverlay, marker);
    } else {
      heroFrame.classList.remove('is-find');
    }
  }
  const findNote = hero.querySelector('.pitch-find__note-close');
  if (findNote) {
    findNote.addEventListener('click', (event) => {
      event.stopPropagation();
      setFind(false);
    });
  }

  heroViewer.addEventListener('ready', () => {
    hero.classList.add('is-ready');
    sizeFindOverlay(findOverlay, hero);
    const spot = heroMarkers.getMarker('km-backpack');
    spot.domElement.addEventListener('pointerenter', () => {
      if (coarsePointer()) return;
      setFind(true);
    });
    spot.domElement.addEventListener('pointerleave', () => {
      if (coarsePointer()) return;
      setFind(false);
    });
  });
  heroViewer.addEventListener('render', () => {
    if (!heroFrame || !heroFrame.classList.contains('is-find')) return;
    syncFindOverlay(findOverlay, heroMarkers.getMarker('km-backpack'));
  });
  window.addEventListener('resize', () => sizeFindOverlay(findOverlay, hero));
  heroMarkers.addEventListener('select-marker', ({ marker }) => {
    if (!marker) return;
    if (marker.id === 'km-backpack') {
      markBackpackFound();
      if (coarsePointer()) setFind(true);
      return;
    }
    openSpot(marker.data);
  });
}

const root = document.getElementById('pitch-viewer');
if (root && root.dataset.panorama) {
  const viewer = new Viewer({
    container: root,
    panorama: root.dataset.panorama,
    navbar: false,
    lang: viewerLang,
    mousewheel: false,
    touchmoveTwoFingers: false,
    defaultYaw: '8deg',
    defaultPitch: '-12deg',
    defaultZoomLvl: 0,
    plugins: [
      MarkersPlugin.withConfig({
        markers: SPOTS.map(markerConfig),
      }),
    ],
  });

  viewer.getPlugin(MarkersPlugin).addEventListener('select-marker', ({ marker }) => {
    const spot = marker && marker.data;
    if (spot && spot.taskId === 'task-choice') resetChoice();
    if (spot && spot.taskId === 'task-text') resetText();
    openSpot(spot);
  });
}

const textButton = document.querySelector('[data-task-text]');
const textAnswer = document.getElementById('task-text-answer');
const textStatus = document.querySelector('[data-task-text-status]');
if (textButton && textAnswer && textStatus) {
  textButton.addEventListener('click', () => {
    const value = textAnswer.value.trim();
    const task = document.getElementById('task-text');
    if (!value) {
      textStatus.textContent = 'Напишите ответ.';
      if (task) task.classList.remove('is-ok', 'is-bad');
      return;
    }
    if (task && task.dataset.accept) {
      const matched = new RegExp(task.dataset.accept, 'i').test(value);
      textStatus.textContent = matched ? task.dataset.ok : task.dataset.bad;
      task.classList.toggle('is-ok', matched);
      task.classList.toggle('is-bad', !matched);
      return;
    }
    textStatus.textContent = 'Ответ принят. В квесте здесь можно сверить текст или просто отметить прохождение.';
    if (task) task.classList.add('is-ok');
  });
}

const choiceButton = document.querySelector('[data-task-choice]');
const choiceStatus = document.querySelector('[data-task-choice-status]');
if (choiceButton && choiceStatus) {
  choiceButton.addEventListener('click', () => {
    const picked = document.querySelector('input[name="pitch-choice"]:checked');
    const task = document.getElementById('task-choice');
    if (!picked) {
      choiceStatus.textContent = 'Выберите вариант.';
      if (task) task.classList.remove('is-ok', 'is-bad');
      document.querySelectorAll('.pitch-options label').forEach((label) => {
        label.classList.remove('is-right', 'is-wrong');
      });
      return;
    }
    const labels = document.querySelectorAll('.pitch-options label');
    labels.forEach((label) => label.classList.remove('is-right', 'is-wrong'));
    const pickedLabel = picked.closest('label');
    const options = document.querySelector('#task-choice .pitch-options');
    const correct = options && options.dataset.correct ? options.dataset.correct : 'i';
    const okText = options && options.dataset.ok ? options.dataset.ok : 'Верно. Кружок i открывает справку.';
    const badText = options && options.dataset.bad ? options.dataset.bad : 'Не этот. Справка — кружок с i.';
    if (picked.value === correct) {
      choiceStatus.textContent = okText;
      if (pickedLabel) pickedLabel.classList.add('is-right');
      if (task) {
        task.classList.add('is-ok');
        task.classList.remove('is-bad');
      }
      return;
    }
    choiceStatus.textContent = badText;
    if (pickedLabel) pickedLabel.classList.add('is-wrong');
    if (task) {
      task.classList.add('is-bad');
      task.classList.remove('is-ok');
    }
  });
}

const puzzleRoot = document.getElementById('pitch-puzzle');
const puzzleStatus = document.querySelector('[data-puzzle-status]');
if (puzzleRoot) {
  const size = 4;
  const board = Array.from({ length: size * size }, (_, index) => (index + 1) % (size * size));
  const scramble = ['left', 'up', 'left', 'up', 'right', 'up', 'left', 'down', 'left', 'up', 'right', 'down'];

  function emptyIndex() {
    return board.indexOf(0);
  }

  function slideEmpty(direction) {
    const empty = emptyIndex();
    const row = Math.floor(empty / size);
    const col = empty % size;
    const next = {
      left: col > 0 ? empty - 1 : -1,
      right: col < size - 1 ? empty + 1 : -1,
      up: row > 0 ? empty - size : -1,
      down: row < size - 1 ? empty + size : -1,
    }[direction];
    if (next < 0) {
      return false;
    }
    board[empty] = board[next];
    board[next] = 0;
    return true;
  }

  scramble.forEach(slideEmpty);

  function solved() {
    return board.every((value, index) => value === (index + 1) % (size * size));
  }

  function tryMove(index) {
    const value = board[index];
    if (!value) return;
    const empty = emptyIndex();
    const sameRow = Math.floor(index / size) === Math.floor(empty / size);
    const sameCol = index % size === empty % size;
    if ((sameRow && Math.abs(index - empty) === 1) || (sameCol && Math.abs(index - empty) === size)) {
      board[empty] = value;
      board[index] = 0;
      renderPuzzle();
    }
  }

  function tileIndex(event) {
    const tile = event.target.closest('button');
    if (!tile || !puzzleRoot.contains(tile) || tile.classList.contains('is-empty')) return -1;
    return [...puzzleRoot.children].indexOf(tile);
  }

  puzzleRoot.addEventListener('pointerdown', (event) => {
    const tile = event.target.closest('button');
    if (!tile || !puzzleRoot.contains(tile) || tile.classList.contains('is-empty')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType !== 'mouse') event.preventDefault();
    tile.setPointerCapture(event.pointerId);
  });

  puzzleRoot.addEventListener('pointerup', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const index = tileIndex(event);
    if (index >= 0) tryMove(index);
  });

  puzzleRoot.addEventListener('click', (event) => {
    if (event.detail !== 0) return;
    const index = tileIndex(event);
    if (index >= 0) tryMove(index);
  });

  function renderPuzzle() {
    puzzleRoot.replaceChildren();
    board.forEach((value) => {
      const tile = document.createElement('button');
      tile.type = 'button';
      if (value === 0) {
        tile.className = 'is-empty';
        tile.tabIndex = -1;
        tile.setAttribute('aria-label', 'Пустая клетка');
      } else {
        tile.textContent = String(value);
        tile.setAttribute('aria-label', `Плитка ${value}`);
      }
      puzzleRoot.append(tile);
    });
    if (puzzleStatus) {
      puzzleStatus.textContent = solved()
        ? 'Собрано. В квесте это отметка о прохождении станции.'
        : 'Сдвиньте плитку рядом с пустой клеткой.';
    }
    const task = document.getElementById('task-puzzle');
    if (task) task.classList.toggle('is-ok', solved());
  }

  renderPuzzle();
}

const cryptexRoot = document.getElementById('pitch-cryptex');
const cryptexStatus = document.querySelector('[data-cryptex-status]');
if (cryptexRoot) {
  const CRYPTEX_WORD = 'КВЕСТ';
  const CRYPTEX_ALPHABET = 'АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЭЮЯ';
  const startOffset = [1, 2, 1, 2, 1];

  function cryptexLetters(letter, index) {
    const pool = [...CRYPTEX_ALPHABET].filter((symbol) => symbol !== letter);
    const picked = [];
    let cursor = index * 5 + 3;
    while (picked.length < 6) {
      cursor = (cursor * 13 + 7) % pool.length;
      picked.push(pool.splice(cursor, 1)[0]);
    }
    picked.splice((index * 2 + 1) % 7, 0, letter);
    return picked;
  }

  const rings = [...CRYPTEX_WORD].map((letter, index) => {
    const letters = cryptexLetters(letter, index);
    const answer = letters.indexOf(letter);
    return {
      letters,
      pos: (answer + startOffset[index]) % letters.length,
    };
  });

  function cryptexWord() {
    return rings.map((ring) => ring.letters[ring.pos]).join('');
  }

  function buildCryptex() {
    cryptexRoot.replaceChildren();
    rings.forEach((_, index) => {
      const column = document.createElement('div');
      column.className = 'pitch-cryptex__ring';
      const up = document.createElement('button');
      up.type = 'button';
      up.className = 'pitch-cryptex__turn';
      up.dataset.ring = String(index);
      up.dataset.dir = '-1';
      up.setAttribute('aria-label', `Кольцо ${index + 1}, повернуть вверх`);
      up.textContent = '▲';
      const slot = document.createElement('div');
      slot.className = 'pitch-cryptex__window';
      const prev = document.createElement('span');
      prev.className = 'pitch-cryptex__side pitch-cryptex__side--prev';
      const current = document.createElement('span');
      current.className = 'pitch-cryptex__current';
      const next = document.createElement('span');
      next.className = 'pitch-cryptex__side pitch-cryptex__side--next';
      slot.append(prev, current, next);
      const down = document.createElement('button');
      down.type = 'button';
      down.className = 'pitch-cryptex__turn';
      down.dataset.ring = String(index);
      down.dataset.dir = '1';
      down.setAttribute('aria-label', `Кольцо ${index + 1}, повернуть вниз`);
      down.textContent = '▼';
      column.append(up, slot, down);
      cryptexRoot.append(column);
    });
  }

  function renderCryptex() {
    rings.forEach((ring, index) => {
      const column = cryptexRoot.children[index];
      const count = ring.letters.length;
      const prev = ring.letters[(ring.pos + count - 1) % count];
      const current = ring.letters[ring.pos];
      const next = ring.letters[(ring.pos + 1) % count];
      column.querySelector('.pitch-cryptex__side--prev').textContent = prev;
      column.querySelector('.pitch-cryptex__current').textContent = current;
      column.querySelector('.pitch-cryptex__side--next').textContent = next;
      column.setAttribute('aria-label', `Кольцо ${index + 1}, буква ${current}`);
    });
    const solved = cryptexWord() === CRYPTEX_WORD;
    if (cryptexStatus) {
      cryptexStatus.textContent = solved
        ? 'Собрано: КВЕСТ. В квесте так открывают шифр на\u00a0точке.'
        : 'Поверните кольцо вверх или вниз.';
    }
    const task = document.getElementById('task-cryptex');
    if (task) task.classList.toggle('is-ok', solved);
  }

  let turnedByPointer = false;

  function turnCryptex(event) {
    const button = event.target.closest('button');
    if (!button || !cryptexRoot.contains(button)) return;
    const index = Number(button.dataset.ring);
    const dir = Number(button.dataset.dir);
    const ring = rings[index];
    if (!ring || !dir) return;
    ring.pos = (ring.pos + dir + ring.letters.length) % ring.letters.length;
    renderCryptex();
  }

  cryptexRoot.addEventListener('pointerdown', (event) => {
    const button = event.target.closest('button');
    if (!button || !cryptexRoot.contains(button)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType !== 'mouse') event.preventDefault();
    button.setPointerCapture(event.pointerId);
  });
  cryptexRoot.addEventListener('pointerup', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    turnedByPointer = true;
    turnCryptex(event);
    setTimeout(() => {
      turnedByPointer = false;
    }, 0);
  });
  cryptexRoot.addEventListener('click', (event) => {
    if (turnedByPointer) return;
    turnCryptex(event);
  });

  buildCryptex();
  renderCryptex();
}

const inquiryModal = document.getElementById('inquiry-modal');
if (inquiryModal) {
  const kindInputs = inquiryModal.querySelectorAll('input[name="kind"]');

  function openInquiry(kind) {
    if (kind) {
      kindInputs.forEach((input) => {
        input.checked = input.value === kind;
      });
    }
    if (!inquiryModal.open) inquiryModal.showModal();
    const name = inquiryModal.querySelector('input[name="name"]');
    if (name) name.focus();
  }

  document.querySelectorAll('[data-inquiry]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openInquiry(trigger.getAttribute('data-kind') || '');
    });
  });

  inquiryModal.addEventListener('click', (event) => {
    if (event.target === inquiryModal) inquiryModal.close();
  });
  inquiryModal.querySelectorAll('[data-inquiry-close]').forEach((button) => {
    button.addEventListener('click', () => inquiryModal.close());
  });

  if (inquiryModal.hasAttribute('data-open')) {
    openInquiry('');
  }
}
